// OT module vocabulary and navigation.
//
// Everything here mirrors a definition the backend owns (ot_common.py). It is
// duplicated on purpose — the frontend needs labels and colours the API does not
// carry — but nothing here DECIDES anything. Whether a case may advance, or the
// board may move, is always the server's answer; these are the words and colours
// used to render it.

import { IPD_COLORS } from "../ipd/utils";

/**
 * The only colours this module uses, and all of them come from IPD_COLORS.
 *
 * `color` is the solid one (a stripe, a bar); `bg`/`text`/`border` make a
 * chip. Four meanings, not a spectrum: nothing here, something happening,
 * something wrong, something finished.
 */
const TONE = {
  neutral: {
    color: IPD_COLORS.textMuted,
    bg: "#F1F5F9",
    text: IPD_COLORS.textSecondary,
    border: IPD_COLORS.border,
  },
  muted: {
    color: IPD_COLORS.textSecondary,
    bg: "#E2E8F0",
    text: IPD_COLORS.textSecondary,
    border: IPD_COLORS.borderHover,
  },
  info: {
    color: IPD_COLORS.info.text,
    bg: IPD_COLORS.info.bg,
    text: IPD_COLORS.info.text,
    border: IPD_COLORS.info.border,
  },
  good: {
    color: "#2E7D32",
    bg: IPD_COLORS.success.bg,
    text: IPD_COLORS.success.text,
    border: IPD_COLORS.success.border,
  },
  warn: {
    color: "#F9A825",
    bg: IPD_COLORS.warning.bg,
    text: IPD_COLORS.warning.text,
    border: IPD_COLORS.warning.border,
  },
  bad: {
    color: "#C62828",
    bg: IPD_COLORS.error.bg,
    text: IPD_COLORS.error.text,
    border: IPD_COLORS.error.border,
  },
};

/** Base path for all OT routes — change here only. */
export const OT_BASE = "/ot_dashboard";

/** The permission every OT screen is gated on (ADR-0007). */
export const OT_PERMISSION = "ot_schedule";

/**
 * Sidebar / tab navigation. Add a screen by appending one entry here and
 * registering its Route in App.js.
 *
 * An entry's optional `permission` narrows it below the module-wide
 * OT_PERMISSION gate every route already sits behind — OtShell filters on it
 * (ADR-0007). pac_queue is narrowed differently: `requiresAnaesthetist`
 * hides it from anyone the anaesthetist roster (036_ot_anaesthetist.sql)
 * does not admit, per GET /ot/me/ (useIsAnaesthetist) — the route itself
 * (OtPacRoute) refuses the same people, so the link must not be offered to
 * someone it would only turn away. This replaced a `permission: "ot_pac"`
 * gate once that permission stopped meaning "anaesthetist" — see that
 * script's header.
 */
export const NAV = [
  { id: "dashboard", label: "Dashboard", route: OT_BASE },
  { id: "schedule", label: "Schedule", route: `${OT_BASE}/schedule` },
  { id: "cases", label: "Cases", route: `${OT_BASE}/cases` },
  { id: "pac_queue", label: "PAC Queue", route: `${OT_BASE}/pac-queue`, requiresAnaesthetist: true },
  { id: "board", label: "Tracking Board", route: `${OT_BASE}/board` },
  { id: "orders", label: "Orders", route: `${OT_BASE}/orders` },
  { id: "final_list", label: "Final List", route: `${OT_BASE}/final-list` },
  { id: "reports", label: "Reports", route: `${OT_BASE}/reports` },
  { id: "masters", label: "Masters", route: `${OT_BASE}/masters`, permission: "ot_admin" },
];

// ── lifecycle ─────────────────────────────────────────────────────────────

/** Lifecycle Status labels. The values are the server's; these are the words. */
export const LIFECYCLE_LABELS = {
  DRAFT: "Draft",
  SCHEDULED: "Scheduled",
  PAC_DONE: "PAC Done",
  APPROVED: "Approved",
  LISTED: "Listed",
  IN_PREOP: "In Pre-op",
  IN_OR: "In Theatre",
  IN_RECOVERY: "In Recovery",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
  POSTPONED: "Postponed",
};

export const LIFECYCLE_ORDER = [
  "DRAFT",
  "SCHEDULED",
  "PAC_DONE",
  "APPROVED",
  "LISTED",
  "IN_PREOP",
  "IN_OR",
  "IN_RECOVERY",
  "CLOSED",
];

/** Moves that need a reason before the server will accept them. */
export const REASON_REQUIRED_FOR = ["CANCELLED", "POSTPONED"];

/** Moves that also need a coded CATEGORY. A free-text reason answers why this
 *  one did not happen; only a coded one answers why ours do not, and the
 *  second is the question a theatre can act on. The categories themselves are
 *  served by fetchLifecycle rather than listed here, so the dropdown can never
 *  offer a value the POST will refuse. */
export const CATEGORY_REQUIRED_FOR = ["CANCELLED"];

// ── list firmness ─────────────────────────────────────────────────────────

/**
 * List Firmness (OT_0022) — the colour-coded label the OT list reads by.
 *
 * DERIVED server-side from lifecycle_status and delivered on every case as
 * `firmness`. Never computed here: a second derivation would be a second
 * opinion, and the two would eventually disagree.
 */
// Drawn from IPD_COLORS rather than invented. Firmness has three states
// worth a colour — not yet firm, firm, and gone — so it gets three, and the
// rest are neutral. A seven-hue palette made every value look equally
// significant, which is the opposite of what a firmness label is for.
export const FIRMNESS = {
  provisional: { label: "Provisional", ...TONE.neutral },
  definite: { label: "Definite", ...TONE.info },
  verified: { label: "Verified", ...TONE.info },
  final: { label: "Final", ...TONE.good },
  closed: { label: "Closed", ...TONE.muted },
  cancelled: { label: "Cancelled", ...TONE.bad },
  postponed: { label: "Postponed", ...TONE.warn },
};

export function firmnessStyle(firmness) {
  return FIRMNESS[firmness] || FIRMNESS.provisional;
}

// ── tracking board ────────────────────────────────────────────────────────

/** The board ladder, in order. Forward-only; the server enforces that. */
export const TRACKING_LADDER = [
  "AWAITING_CALL",
  "TO_CALL",
  "CALLED",
  "CHECKED_IN",
  "AWAITING_PROCEDURE",
  "IN_PROGRESS",
  "COMPLETED",
  "IN_RECOVERY",
  "CHECKED_OUT",
];

export const TRACKING_LABELS = {
  AWAITING_CALL: "Awaiting Call",
  TO_CALL: "To Call",
  CALLED: "Called",
  CHECKED_IN: "Checked In",
  AWAITING_PROCEDURE: "Awaiting Procedure",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  IN_RECOVERY: "In Recovery",
  CHECKED_OUT: "Checked Out",
};

/**
 * Board tones. Nine rungs, FOUR bands — because the useful question a board
 * answers is not which of nine rungs someone is on, it is whether they are
 * still elsewhere, waiting in the suite, on the table, or through.
 *
 * Nine separate hues made every rung shout equally loudly; the one that
 * actually needs to be seen from across the room is IN_PROGRESS.
 */
export const TRACKING_TONE = {
  AWAITING_CALL: TONE.neutral,
  TO_CALL: TONE.neutral,
  CALLED: TONE.neutral,
  CHECKED_IN: TONE.info,
  AWAITING_PROCEDURE: TONE.info,
  IN_PROGRESS: TONE.bad,
  COMPLETED: TONE.good,
  IN_RECOVERY: TONE.good,
  CHECKED_OUT: TONE.muted,
};

export function trackingTone(status) {
  return TRACKING_TONE[status] || TONE.neutral;
}

// ── case vocabulary ───────────────────────────────────────────────────────

export const CASE_SOURCES = [
  { value: "OP", label: "Outpatient" },
  { value: "ER", label: "Emergency" },
  { value: "IPD", label: "Inpatient" },
];

export const SCHEDULING_TYPES = [
  { value: "ELECTIVE", label: "Elective" },
  { value: "EMERGENCY", label: "Emergency" },
  { value: "DAYCARE", label: "Daycare" },
];

export const PAC_OUTCOMES = [
  { value: "FIT", label: "Fit for surgery" },
  { value: "UNFIT", label: "Unfit for surgery" },
  { value: "OPTIMIZE", label: "Medical Optimization Required" },
];

export const CONSENT_STATUSES = [
  { value: "PENDING", label: "Pending" },
  { value: "OBTAINED", label: "Obtained" },
  { value: "REFUSED", label: "Refused" },
  { value: "WAIVED", label: "Waived" },
];

/** main_servicegroup ids the order-numbering signal knows about. */
export const LAB_SERVICE_GROUP_ID = 10;
export const RADIOLOGY_SERVICE_GROUP_ID = 1;

/**
 * Which department an investigation will actually be sent to, or null.
 *
 * Mirrors `order_routing_for` in main/patient/manage_lab_test.py — the backend
 * function that assigns the order number. A service it refuses produces a
 * PatientLabTest with no order_no, and a row with no order_no appears in no
 * worklist, so the order is silently lost. That is the whole reason this
 * exists on the front end: the picker can leave those services out instead of
 * letting somebody order one and never hear about it again.
 *
 * The GROUP decides. service_type answers only when there is no group at all —
 * forty x-rays on this tenant are typed TEST and filed under Radiology, and
 * the group is right about them where the type is not.
 */
export function investigationDepartment(service) {
  if (!service) return null;
  const group = service.service_group_id;
  const groupId = group === null || group === undefined || group === "" ? null : Number(group);
  if (groupId === RADIOLOGY_SERVICE_GROUP_ID) return "radiology";
  if (groupId === LAB_SERVICE_GROUP_ID) return "laboratory";
  if (groupId !== null) return null;

  const type = String(service.service_type || "").trim().toUpperCase();
  if (type === "RADIOLOGY") return "radiology";
  if (type === "TEST") return "laboratory";
  return null;
}

export const ORDER_TYPES = [
  { value: "LAB", label: "Laboratory" },
  { value: "IMAGING", label: "Imaging" },
  { value: "MEDICINE", label: "Medicine" },
];

export const ORDER_STAGES = [
  { value: "INTRA_OP", label: "Intra-op" },
  { value: "POST_OP", label: "Post-op" },
];

// The service-master classifications OT bills against, by NAME. The numeric
// main_serviceclassification.id differs per tenant schema; the name does not,
// so a screen that hardcodes the name works on every tenant and one that
// hardcodes 81 silently bills the wrong thing on the next.
export const CLASSIFICATION_OT_ROOMS = "OT Room Charges";
export const CLASSIFICATION_OT_SURGERIES = "OT Surgical Procedures";
export const CLASSIFICATION_OT_CONSUMABLES = "OT Consumables";

/**
 * What can be charged for an operation, and where each one lives in the
 * service master.
 *
 * `kind` is the CaseCharge.charge_kind the line is stored under — THEATRE is
 * the only one priced off the clock, PROCEDURE is the operation itself, and
 * everything else is OTHER. The classification is what the picker searches.
 */
export const OT_CHARGE_CATEGORIES = [
  {
    id: "THEATRE",
    label: "Theatre charges",
    classification: CLASSIFICATION_OT_ROOMS,
    kind: "THEATRE",
    hint: "Priced per 30 minutes of in-room time.",
  },
  {
    id: "SURGERY",
    label: "Surgery",
    classification: CLASSIFICATION_OT_SURGERIES,
    kind: "PROCEDURE",
    // This line IS a named person's fee, so it cannot post without one.
    practitioner: true,
    hint: "The named operation. This line IS the operating surgeon's fee — do not bill them again under Professional fees.",
  },
  {
    id: "PROFESSIONAL",
    label: "Professional fees",
    classification: "OT Professional Charges",
    kind: "OTHER",
    practitioner: true,
    hint: "Assistant surgeon, co-surgeon, anaesthetist, perfusionist, scrub nurse, OT technician. Not the operating surgeon.",
  },
  {
    id: "ANAESTHESIA",
    label: "Anaesthesia",
    classification: "OT Anaesthesia Charges",
    kind: "OTHER",
    practitioner: true,
    hint: "The technique and the gases. The anaesthetist's own fee is a professional fee.",
  },
  {
    id: "EQUIPMENT",
    label: "Equipment",
    classification: "OT Equipment Charges",
    kind: "OTHER",
    hint: "C-arm, laparoscopy set, harmonic scalpel, bypass machine.",
  },
  {
    id: "SUPPORT",
    label: "Theatre support",
    classification: "OT Support Charges",
    kind: "OTHER",
    hint: "Recovery room, CSSD, frozen section, after-hours loading.",
  },
];

export const CONSUMPTION_TYPES = [
  { value: "SUTURE", label: "Suture" },
  { value: "SWAB", label: "Swab" },
  { value: "IMPLANT", label: "Implant" },
  { value: "DISPOSABLE", label: "Disposable" },
  { value: "DRUG", label: "Drug" },
  { value: "OTHER", label: "Other" },
];

export const COUNT_ITEM_TYPES = [
  { value: "SWAB", label: "Swab" },
  { value: "INSTRUMENT", label: "Instrument" },
  { value: "NEEDLE", label: "Needle" },
  { value: "SHARP", label: "Sharp" },
  { value: "OTHER", label: "Other" },
];

/** AORN counting junctures. A count at closure alone is two counts and an
 *  assumption; these are the moments the standard actually asks for.
 *
 *  `hint` matters: at ADDED the number is how many were opened, and everywhere
 *  else it is the total on the field. Getting that the wrong way round is the
 *  easiest mistake to make at the keyboard, so the form says which it wants. */
export const COUNT_JUNCTURES = [
  { value: "ADDED", label: "Items added", hint: "How many were opened onto the field" },
  { value: "CAVITY_CLOSURE", label: "Before cavity closure", hint: "Total on the field now" },
  { value: "SKIN_CLOSURE", label: "At skin closure", hint: "Total on the field now" },
  { value: "STAFF_CHANGE", label: "Staff handover", hint: "Total on the field now" },
  { value: "FINAL", label: "Final count", hint: "Total on the field now" },
];

export const COUNT_RESULTS = {
  PENDING: { label: "Not counted", ...TONE.neutral },
  CORRECT: { label: "Correct", ...TONE.good },
  DISCREPANCY: { label: "DISCREPANCY", ...TONE.bad },
};

// ── safety & quality vocabulary ───────────────────────────────────────────
//
// These come from published standards rather than from OT Requirements.xlsx.
// Each is coded rather than free text for the same reason: it only earns its
// keep when it can be counted, and prose does not aggregate.

/** CDC surgical wound classification — one third of the NHSN surgical-site-
 *  infection risk index, with ASA class and procedure duration. */
export const WOUND_CLASSES = [
  { value: "CLEAN", label: "Clean" },
  { value: "CLEAN_CONTAMINATED", label: "Clean-contaminated" },
  { value: "CONTAMINATED", label: "Contaminated" },
  { value: "DIRTY", label: "Dirty / infected" },
];

/** ASA physical status. The E modifier is a separate flag, not a seventh
 *  class — an ASA II E is not an ASA III. */
export const ASA_CLASSES = [
  { value: "I", label: "I — a normally healthy patient" },
  { value: "II", label: "II — mild systemic disease" },
  { value: "III", label: "III — severe systemic disease" },
  { value: "IV", label: "IV — a constant threat to life" },
  { value: "V", label: "V — moribund, not expected to survive the operation" },
  { value: "VI", label: "VI — brain-dead, organs being removed for donation" },
];

/** Coded because a wrong-side review cannot filter on prose: R, Rt and right
 *  are the same answer written three ways. */
export const LATERALITIES = [
  { value: "LEFT", label: "Left" },
  { value: "RIGHT", label: "Right" },
  { value: "BILATERAL", label: "Bilateral" },
  { value: "NOT_APPLICABLE", label: "Not applicable" },
];

/** The surgical fire triad, assessed at Time-Out. All three present in one
 *  field is the recognised high-risk configuration. */
export const FIRE_RISK_ITEMS = [
  { key: "to_fire_risk_oxidiser", label: "Oxidiser — O2 or N2O in the field" },
  { key: "to_fire_risk_ignition", label: "Ignition source — diathermy, laser, drill" },
  { key: "to_fire_risk_fuel", label: "Fuel — alcohol prep, drapes, hair" },
];

export const RECOVERY_DESTINATIONS = [
  { value: "WARD", label: "Back to ward" },
  { value: "ICU", label: "Transfer to ICU" },
  { value: "HOME", label: "Home (daycare)" },
];

export const MAINTENANCE_TYPES = [
  { value: "CLEANING", label: "Cleaning" },
  { value: "FUMIGATION", label: "Fumigation" },
  { value: "STERILISATION", label: "Sterilisation" },
  { value: "MAINTENANCE", label: "Maintenance" },
];

export const WHO_SECTIONS = [
  { value: "SIGN_IN", label: "Sign In", when: "Before induction of anaesthesia" },
  { value: "TIME_OUT", label: "Time Out", when: "Before skin incision" },
  { value: "SIGN_OUT", label: "Sign Out", when: "Before the patient leaves theatre" },
];

/** Modified Aldrete: five parameters, each 0-2, dischargeable at 9 of 10. */
export const ALDRETE_PARAMS = [
  { key: "activity", label: "Activity" },
  { key: "respiration", label: "Respiration" },
  { key: "circulation", label: "Circulation" },
  { key: "consciousness", label: "Consciousness" },
  { key: "oxygen_saturation", label: "Oxygen saturation" },
];
export const ALDRETE_DISCHARGEABLE_AT = 9;

// The first six are the requirement's registers. The last three were never
// requirements, which is rather the point: the sheet specifies a theatre's
// billing thoroughly and its safety and efficiency not at all. Each of the
// three is computed from data the module already captured.
// `asks` is what the screen shows. `requirement` is traceability back to the
// sheet and stays out of the interface — a nurse reading "requirement OT_0086"
// learns nothing they can use.
export const REPORTS = [
  {
    id: "or-register",
    label: "OR Register",
    requirement: "OT_0083",
    asks: "Chronological operating room log with validated entry, exit, and surgical incision timestamps.",
  },
  {
    id: "surgery-bill",
    label: "Surgery Bill Details",
    requirement: "OT_0084",
    asks: "Itemized surgical procedure billing, professional charges, and billing practitioner ledger.",
  },
  {
    id: "billed-unbilled",
    label: "Billed / Unbilled",
    requirement: "OT_0085",
    asks: "Reconciliation of posted vs unbilled theatre charges, consumable items, and accruals.",
  },
  {
    id: "procedure-time",
    label: "Procedure Time",
    requirement: "OT_0086",
    asks: "Variance analysis comparing scheduled theatre duration against actual procedure execution time.",
  },
  {
    id: "procedure-numeric",
    label: "Procedure Numbers",
    requirement: "OT_0087",
    asks: "Procedure volume metrics, specialty categorization, and cumulative tariff totals.",
  },
  {
    id: "surgeon-wise",
    label: "Surgeon-wise",
    requirement: "OT_0088",
    asks: "Surgeon surgical volume breakdown: elective vs emergency cases, and cancellation rates.",
  },
  {
    id: "cancellations",
    label: "Cancellations",
    requirement: "Quality",
    asks: "Clinical cancellation audit: root-cause breakdown, postponement trends, and avoidability classifications.",
  },
  {
    id: "checklist-compliance",
    label: "Checklist Compliance",
    requirement: "Safety",
    asks: "WHO Surgical Safety Checklist completion rates, sign-in/time-out audit, and prophylactic antibiotic timing.",
  },
  {
    id: "or-efficiency",
    label: "OR Efficiency",
    requirement: "Management",
    asks: "Operating suite performance: first-case on-time starts, inter-case turnaround intervals, and room utilization.",
  },
];

/** Turn a snake_case checklist key into the words a nurse reads. */
export function humanise(key) {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}
